import {
  Button,
  Card,
  FormControl,
  FormControlLabel,
  Grid,
  ListItem,
  ListItemAvatar,
  ListItemIcon,
  ListItemText,
  Radio,
  RadioGroup,
  Typography,
} from '@material-ui/core';
import InputBase from '@material-ui/core/InputBase';
import { fade, makeStyles } from '@material-ui/core/styles';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import FolderIcon from '@material-ui/icons/Folder';
import SearchIcon from '@material-ui/icons/Search';
import React, { useEffect, useState } from 'react';
import { getPreviewVideosCatalogue, getPreviewVideosList } from 'src/api/subjectsApi';
import Label from 'src/components/Label';
import { Sidebar } from 'src/reuseableComponents';

const useStyle = makeStyles(theme => ({
  cardContainer: {
    padding: '12px 2px !important',
  },
  eachSubjet: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '#fff',
    border: '1px solid #c7c3c300',
    borderRadius: '4px',
    height: '5.5rem',
    cursor: 'pointer',
  },
  eachSubjetSelected: {
    backgroundColor: '#E3F2FD',
    display: 'flex',
    alignItems: 'center',
    border: '4px solid #E3F2FD',
    borderRadius: '4px',
    height: '5.5rem',
  },
  listDetail: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
  },
  listName: {
    width: '100%',
    paddingRight: theme.spacing(1),
  },
  backButton: {
    left: '0px',
    bottom: '9px',
  },
  search: {
    top: '-65px',
    width: 'calc(100% - 128px)',
    display: 'inline-flex',
    borderRadius: theme.shape.borderRadius,
    backgroundColor: fade(theme.palette.background.default, 0.75),
    '&:hover': {
      backgroundColor: fade(theme.palette.background.default, 1),
    },
    marginLeft: 0,
    marginBottom: '10px',
  },
  searchIcon: {
    padding: theme.spacing(0, 2),
    height: '2rem',
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  inputRoot: {
    color: 'inherit',
  },
  inputInput: {
    padding: theme.spacing(1, 1, 1, 0),

    transition: theme.transitions.create('width'),
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      width: '12ch',
      '&:focus': {
        width: '20ch',
      },
    },
  },
}));

const SearchBar = ({ searchTerm, handleSearch, visible }) => {
  const classes = useStyle();
  return (
    <div className={classes.search} style={{ visibility: !visible ? 'hidden' : 'visible' }}>
      <div className={classes.searchIcon}>
        <SearchIcon />
      </div>
      <InputBase
        placeholder="Search…"
        classes={{
          root: classes.inputRoot,
          input: classes.inputInput,
        }}
        value={searchTerm}
        onChange={handleSearch}
        inputProps={{ 'aria-label': 'search' }}
      />
    </div>
  );
};

const LabelComponent = ({ status }) => {
  switch (status) {
    case 'COMPLETE':
      return <Label color="success">Transcoded</Label>;

    case 'PROGRESSING':
      return <Label color="warning">TRANSCODING IN PROGRESS</Label>;

    default:
      return <Label color="" />;
  }
};

const ListDetail = ({ item }) => {
  const classes = useStyle();

  return (
    <div className={classes.listDetail}>
      <Typography variant="body2" color="initial" className={classes.listName}>
        {item.Name}
      </Typography>
      <LabelComponent status={item.transcodingStatus} />
    </div>
  );
};

const selectedFileData = (filesList, fileId) => {
  const selectedFile = filesList.find(file => file.Id === fileId);
  return selectedFile;
};

const FileView = ({ list, onSelectedFileChange, selectedFile }) => {
  const handleChange = event => {
    const fileId = event.target.value;
    onSelectedFileChange(fileId, selectedFileData(list, fileId));
  };

  return (
    <FormControl component="fieldset">
      <RadioGroup aria-label="files" name="files" value={selectedFile} onChange={handleChange}>
        {list.map(item => (
          <FormControlLabel
            key={item.Id}
            value={item.Id}
            control={<Radio />}
            label={<ListDetail item={item} />}
            disabled={!(item.transcodingStatus === 'COMPLETE')}
          />
        ))}
      </RadioGroup>
    </FormControl>
  );
};

const FolderView = ({ list, selectedFolderId, onFolderClick, onFolderDbClick }) => {
  const classes = useStyle();
  return (
    <Grid container>
      {list.map(item => (
        <Grid item xs={12} key={item.Id} className={classes.cardContainer}>
          <Card
            className={
              item.Id === selectedFolderId ? classes.eachSubjetSelected : classes.eachSubjet
            }
            onClick={() => onFolderClick(item.Id)}
            onDoubleClick={() => onFolderDbClick(item.Id)}
          >
            <ListItem>
              <ListItemAvatar>
                <ListItemIcon>
                  {item.Id === selectedFolderId ? (
                    <FolderIcon fontSize="large" style={{ color: '#2196F3' }} />
                  ) : (
                    <FolderIcon fontSize="large" />
                  )}
                </ListItemIcon>
              </ListItemAvatar>
              <ListItemText
                primary={
                  item.Id === selectedFolderId ? (
                    <Typography variant="h6" style={{ color: '#2196F3' }}>
                      {item.Name}
                    </Typography>
                  ) : (
                    <Typography variant="h6">{item.Name}</Typography>
                  )
                }
              />
            </ListItem>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};

const FileExplorer = ({
  heading,
  selectedFolderId,
  setSelectedFolderId,
  selectedFile,
  handleFileSelect,
  onSelectedFileChange,
  onResetFileSelect,
  onResetThumbnail,
  viewType,
  handleViewType,
  children,
}) => {
  const classes = useStyle();
  const [searchTerm, setSearchTerm] = useState('');
  const [folders, setFolders] = useState([]);
  const [currentData, setCurrentData] = useState([]); // dynamic data for rendering

  useEffect(() => {
    fetchFolders();
  }, []);

  const fetchFolders = async () => {
    const previewData = await getPreviewVideosCatalogue();
    const foldersList = previewData.map(item => ({
      Name: item.catalogueName,
      Id: item.catalogueId,
    }));
    setFolders(foldersList);
    setCurrentData(foldersList);
  };

  useEffect(() => {
    if (viewType === 'Files') {
      setSearchTerm('');
      setCurrentData([]);
      onFolderClick(selectedFolderId);
    }
    // eslint-disable-next-line
  }, [viewType]);

  const onFolderClick = async folderId => {
    const videosList = await getPreviewVideosList(folderId);
    const filesList = videosList.fileList.map(item => ({
      Name: item.file,
      Id: item.awsKey,
      Length: item.size,
      endpoints: item.endpoints,
      videoDurationInSecond: item.videoDurationInSecond,
      transcodingStatus: item.transcodingStatus,
      catalogueId: item.catalogueId,
      videoId: item.videoId,
      subjectId: item.subjectId,
      jobId: item.jobId,
      uploadedBy: item.uploadedBy,
      uploadedAt: item.uploadedAt,
    }));
    handleViewType('Files');
    setCurrentData(filesList);
    if (filesList > 0) {
      handleFileSelect(filesList[0].Id, filesList[0]);
    }
  };

  const onBackClick = () => {
    if (viewType === 'Thumbnails') {
      handleViewType('Files');
      onResetThumbnail();
    } else {
      handleViewType('Folders');
      setCurrentData(folders);
      setSearchTerm('');
      onResetFileSelect();
    }
  };

  const handleSearch = e => {
    setSearchTerm(e.target.value);
  };

  const filteredData = currentData.filter(
    item => item.Name && item.Name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <>
      <Typography variant="h6" color="initial" style={{ padding: '0.5rem 0' }}>
        {heading}
      </Typography>
      <Button
        variant="text"
        color="default"
        className={classes.backButton}
        onClick={onBackClick}
        style={{ visibility: viewType === 'Folders' ? 'hidden' : 'visible' }}
      >
        <ArrowBackIcon />
      </Button>
      <SearchBar
        searchTerm={searchTerm}
        handleSearch={handleSearch}
        visible={viewType !== 'Thumbnails'}
      />
      {viewType === 'Folders' && (
        <FolderView
          list={filteredData}
          selectedFolderId={selectedFolderId}
          onFolderClick={setSelectedFolderId}
          onFolderDbClick={onFolderClick}
        />
      )}
      {viewType === 'Files' && (
        <FileView
          list={filteredData}
          onSelectedFileChange={onSelectedFileChange}
          selectedFile={selectedFile}
        />
      )}
      {viewType === 'Thumbnails' && children}
    </>
  );
};

function CustomFileExplorer({
  open,
  handleClose,
  onResetThumbnail,
  func,
  handleAddVideo,
  children,
  isAttachable,
}) {
  const [viewType, setViewType] = useState('Folders');
  const [selectedFolderId, setSelectedFolderId] = useState('');
  const [selectedFile, setSelectedFile] = React.useState({
    Id: '',
    File: {},
  });

  const handleViewType = view => {
    setViewType(view);
  };

  const onSelectedFileChange = (fileId, file) => {
    setSelectedFile({
      Id: fileId,
      File: file,
    });
  };

  const handleFileSelect = (fileId, file) => {
    setSelectedFile({
      Id: fileId,
      File: file,
    });
  };

  const handleDrawerSubmit = () => {
    if (viewType === 'Folders') {
      setViewType('Files');
    } else if (viewType === 'Files') {
      if (func) {
        func(selectedFile.File);
      }
      setViewType('Thumbnails');
    } else if (viewType === 'Thumbnails') {
      handleAddVideo();
      handleCloseSidebar();
    }
  };

  const handleCloseSidebar = () => {
    handleClose();
    setSelectedFolderId('');
    handleResetFileSelect();
    setViewType('Folders');
  };

  const handleResetFileSelect = () => {
    setSelectedFile({
      Id: '',
      File: {},
    });
  };

  const stepMessage = () => {
    switch (viewType) {
      case 'Folders':
        return {
          title: 'Showing folders containing demo videos',
          subtitle: 'Select the folder',
        };

      case 'Files':
        return {
          title: 'Showing demo videos',
          subtitle: 'Select the video file',
        };

      case 'Thumbnails':
        return {
          title: 'Select the auto-generated thumnnail image',
          subtitle: 'Demo Video Title',
        };

      default:
        break;
    }
  };

  return (
    <Sidebar
      open={open}
      onClose={handleCloseSidebar}
      title="Browse Demo Videos"
      subtitle={stepMessage().title}
      primaryButtonText={viewType !== 'Thumbnails' ? 'NEXT STEP' : 'ADD VIDEO'}
      primaryButtonAction={handleDrawerSubmit}
      primaryButtonDisabled={
        (viewType === 'Folders' && selectedFolderId === '') ||
        (viewType === 'Files' && selectedFile.Id === '') ||
        (viewType === 'Thumbnails' && !isAttachable)
      }
      secondaryButtonText="EXIT MENU"
      secondaryButtonAction={handleCloseSidebar}
      secondaryButtonDisabled={false}
    >
      <FileExplorer
        heading={stepMessage().subtitle}
        selectedFolderId={selectedFolderId}
        setSelectedFolderId={setSelectedFolderId}
        selectedFile={selectedFile.Id}
        handleFileSelect={handleFileSelect}
        onSelectedFileChange={onSelectedFileChange}
        onResetFileSelect={handleResetFileSelect}
        onResetThumbnail={onResetThumbnail}
        viewType={viewType}
        handleViewType={handleViewType}
        // eslint-disable-next-line react/no-children-prop
        children={children}
      />
    </Sidebar>
  );
}

export { CustomFileExplorer };
